import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, MessageCircle, Send, X } from "lucide-react";

function getSessionId() {
  let id = localStorage.getItem("eh_chat_sid");
  if (!id) {
    id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("eh_chat_sid", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi 👋 I'm EH Inc's assistant. Ask me about property, marriage contracts, wills, trusts, deceased estates — or how to get started.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: `Sorry — ${formatApiError(err)}` }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-[72px] right-6 z-[9998] h-14 w-14 bg-zinc-900 text-white grid place-items-center shadow-lg hover:bg-zinc-800 transition-colors"
        aria-label="Open chat"
        data-testid="chat-toggle"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[140px] right-6 z-[9998] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] bg-white border border-zinc-200 shadow-2xl flex flex-col"
          data-testid="chat-panel"
        >
          <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#0055FF]" />
            <div>
              <div className="text-sm font-semibold">EH Inc AI Assistant</div>
              <div className="text-[10px] text-zinc-500 tracking-wider uppercase">General information · not legal advice</div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 leading-relaxed ${
                    m.role === "user" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-100 text-zinc-500 px-3 py-2 text-sm">Thinking…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="border-t border-zinc-200 p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about transfer duty, OTP, FICA…"
              className="flex-1 px-3 py-2 border border-zinc-200 text-sm outline-none focus:border-zinc-900 transition-colors"
              data-testid="chat-input"
            />
            <button
              onClick={send}
              disabled={loading}
              className="bg-zinc-900 text-white p-2 hover:bg-zinc-800 transition-colors disabled:opacity-50"
              data-testid="chat-send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
